import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useQuote } from "@/context/QuoteContext";

const SLIDE_IMAGES = [
  [
    "https://images.unsplash.com/photo-1586363104862-3a5e2ab60d99?crop=entropy&cs=srgb&fm=jpg&w=800&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1624548140150-108c3287f551?crop=entropy&cs=srgb&fm=jpg&w=600&q=85&ixlib=rb-4.1.0",
  ],
  [
    "https://images.unsplash.com/photo-1600823921193-c388313a14a5?crop=entropy&cs=srgb&fm=jpg&w=800&q=85&ixlib=rb-4.1.0",
    "https://images.pexels.com/photos/15759553/pexels-photo-15759553.jpeg?auto=compress&cs=tinysrgb&w=600",
  ],
  [
    "https://images.pexels.com/photos/9965135/pexels-photo-9965135.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.unsplash.com/photo-1714317438040-0e8584215699?crop=entropy&cs=srgb&fm=jpg&w=600&q=85&ixlib=rb-4.1.0",
  ],
];

const ease = [0.76, 0, 0.24, 1];

function BackgroundShapes() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <motion.div
        animate={{ y: [0, 30, 0], x: [0, -20, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-primary/[0.04]"
      />
      <motion.div
        animate={{ y: [0, -25, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full border border-foreground/[0.05]"
      />
      <div className="absolute right-1/4 top-1/3 h-40 w-40 rotate-45 border border-foreground/[0.04]" />
      <div
        className="absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)/0.025) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)/0.025) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />
    </div>
  );
}

function FloatingImages({ images, active }) {
  return (
    <div className="relative hidden h-full w-full items-center justify-center lg:flex">
      <motion.div
        key={`a-${active}`}
        initial={{ opacity: 0, y: 60, rotate: -3 }}
        animate={{ opacity: 1, y: 0, rotate: -3 }}
        transition={{ duration: 0.9, ease, delay: 0.15 }}
        className="animate-float"
      >
        <div className="relative h-[440px] w-[340px] overflow-hidden bg-secondary shadow-2xl ring-1 ring-black/5">
          <img src={images[0]} alt="" className="h-full w-full object-cover" />
        </div>
      </motion.div>
      <motion.div
        key={`b-${active}`}
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease, delay: 0.35 }}
        className="absolute -bottom-6 end-8 animate-float"
        style={{ animationDelay: "1.2s" }}
      >
        <div className="relative h-[220px] w-[190px] overflow-hidden bg-background shadow-2xl ring-1 ring-black/5">
          <img src={images[1]} alt="" className="h-full w-full object-cover" />
        </div>
      </motion.div>
      <div className="absolute end-2 top-6 z-[-1] font-display text-[10rem] font-bold leading-none text-foreground/[0.03]">
        0{active + 1}
      </div>
    </div>
  );
}

export default function HeroSlider() {
  const { t, dir } = useI18n();
  const { openQuote } = useQuote();
  const slides = t("hero.slides");

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", direction: dir === "rtl" ? "rtl" : "ltr" },
    [Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section
      data-testid="hero-slider"
      className="relative h-[680px] w-full overflow-hidden bg-background md:h-[900px]"
    >
      <BackgroundShapes />
      <div className="h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, i) => (
            <div className="relative h-full min-w-0 flex-[0_0_100%]" key={i}>
              <div className="mx-auto grid h-full max-w-[1400px] grid-cols-1 items-center gap-8 px-5 pt-40 md:px-10 md:pt-48 lg:grid-cols-12">
                <div className="z-10 lg:col-span-6">
                  {selected === i && (
                    <motion.div
                      key={`tag-${i}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                      className="mb-6 inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground"
                    >
                      <span className="h-1.5 w-1.5 bg-primary" />
                      {slide.tag}
                    </motion.div>
                  )}
                  <h1 className="font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight md:text-7xl xl:text-8xl">
                    {slide.title.map((line, li) => (
                      <span key={li} className="block overflow-hidden">
                        {selected === i && (
                          <motion.span
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.8, ease, delay: 0.15 + li * 0.12 }}
                            className="block"
                          >
                            {line === slide.title[slide.title.length - 1] ? (
                              <span className="text-primary">{line}</span>
                            ) : (
                              line
                            )}
                          </motion.span>
                        )}
                      </span>
                    ))}
                  </h1>
                  {selected === i && (
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, delay: 0.55 }}
                      className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg"
                    >
                      {slide.desc}
                    </motion.p>
                  )}
                  {selected === i && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.75 }}
                      className="mt-9 flex flex-wrap items-center gap-4"
                    >
                      <button
                        data-testid="hero-primary-btn"
                        onClick={() => document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" })}
                        className="group flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary/30"
                      >
                        {slide.primary}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
                      </button>
                      <button
                        data-testid="hero-secondary-btn"
                        onClick={() => openQuote(null)}
                        className="flex items-center gap-2 rounded-full border border-foreground/25 px-7 py-3.5 text-sm font-semibold transition-[transform,background-color,color] duration-300 hover:scale-105 hover:bg-foreground hover:text-background"
                      >
                        {slide.secondary}
                      </button>
                    </motion.div>
                  )}
                </div>
                <div className="lg:col-span-6">
                  <FloatingImages images={SLIDE_IMAGES[i]} active={selected} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        data-testid="hero-prev"
        onClick={scrollPrev}
        aria-label="Previous"
        className="absolute bottom-8 start-5 z-20 flex h-11 w-11 items-center justify-center border border-border bg-background/60 backdrop-blur-md transition-colors hover:bg-foreground hover:text-background md:start-10"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
      </button>
      <button
        data-testid="hero-next"
        onClick={scrollNext}
        aria-label="Next"
        className="absolute bottom-8 start-20 z-20 flex h-11 w-11 items-center justify-center border border-border bg-background/60 backdrop-blur-md transition-colors hover:bg-foreground hover:text-background md:start-24"
      >
        <ArrowRight className="h-4 w-4 rtl:rotate-180" />
      </button>

      <div className="absolute inset-x-0 bottom-11 z-20 flex items-center justify-center gap-2" data-testid="hero-dots">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => emblaApi?.scrollTo(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-1.5 transition-all duration-300 ${
              selected === i ? "w-8 bg-primary" : "w-4 bg-foreground/25 hover:bg-foreground/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}